import type { UIEvent } from "react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components";

import { swSettings } from "app/common/services/swSettings";
import { useAutoScrollTop } from "app/custom-hooks/useAutoScrollTop";
import { isTraditionalTrialUser } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

const MAX_DYNAMIC_STICKINESS_HEIGHT = 900;
const CLOSE_DELAY = 2500;
const POPUP_PREFIX = "div[id^=popupRootID_]";

//a list of popups that should keep the subnav sticky when exist in the hierarchy
const FILTER_POPUP_SELECTORS = [
    `${POPUP_PREFIX} .FiltersBarDropdown`,
    `${POPUP_PREFIX} .DurationSelector-popup-container`,
];

//a list of subnav children that should keep it sticky when exist in the hierarchy
const OVERFLOWING_SUBNAV_CHILDREN_ELEMENTS = [
    "div.AutocompleteWebsitesQueryBar",
    "div.Popup-Container-infoCard",
    "div.AutoCompleteContainer",
    "div.ListItemsContainer",
];

//e.g. :not(:has(div.class1)):not(:has(div.class2)):not(:has(div.class3))...
const OVERFLOWING_SUBNAV_CHILDREN_NEGATION_CLAUSE = OVERFLOWING_SUBNAV_CHILDREN_ELEMENTS.reduce(
    (prev, curr) => `${prev}:not(:has(${curr}))`,
    "",
);

const isStickySubNavEnabled = () => isTraditionalTrialUser(swSettings);

const StickySubNav = styled.div<ISubNavWrapper>`
    --subnav-overflow: unset;
    --subnav-translate: none;

    @media screen and (max-height: ${MAX_DYNAMIC_STICKINESS_HEIGHT}px) {
        /* only apply when all overflowing elements in the subnav are closed */
        ${OVERFLOWING_SUBNAV_CHILDREN_NEGATION_CLAUSE} {
            --subnav-overflow: ${({ isSticky }) => (isSticky ? "unset" : "hidden")};
            --subnav-translate: ${({ isSticky }) => (isSticky ? "none" : "0 -100%")};
        }
    }

    position: absolute;
    width: 100%;
    overflow: var(--subnav-overflow);
    transition: translate 0.3s;

    /* force stickiness on hover when the nav is open */
    &:not(:hover) {
        translate: var(--subnav-translate);
    }
`;

interface ISubNavWrapper {
    subNavRef: React.MutableRefObject<any>;
    isSticky: boolean;
    className?: string;
    children?: React.ReactNode;
}

interface IPageContentWrapper extends React.HTMLProps<HTMLDivElement> {
    handleScroll: (ev: UIEvent<HTMLDivElement>) => void;
    navHeight: number;
}

interface IStickySubNavProps {
    SubNavWrapper: (props: ISubNavWrapper) => React.ReactElement;
    PageContentWrapper: (props: IPageContentWrapper) => React.ReactElement;
    subNavWrapperProps: ISubNavWrapper;
    pageContentWrapperProps: IPageContentWrapper;
}

const useStickySubNav = (): IStickySubNavProps => {
    const enabled = isStickySubNavEnabled();
    const subNavRef = useRef<HTMLDivElement>(null);
    const [alwaysSticky, setAlwaysSticky] = useState<boolean>(false);
    const [lastScrollHeight, setLastScrollHeight] = useState<number>(0);
    const [scrollDelta, setScrollDelta] = useState<number>(0);
    const [navHeight, setNavHeight] = useState<number>(0);
    const [closeTimeout, setCloseTimeout] = useState<ReturnType<typeof setTimeout>>();
    const [pageHeight, setPageHeight] = useState<number>(window.innerHeight);
    const pageContentRef = useAutoScrollTop();
    const isSticky = useMemo<boolean>(
        () => !enabled || alwaysSticky || scrollDelta < 0 || lastScrollHeight <= navHeight || !!closeTimeout,
        [enabled, alwaysSticky, scrollDelta, lastScrollHeight, navHeight, closeTimeout],
    );

    //keep track of changes in the page
    useEffect(() => {
        if (enabled) {
            const handleWindowResize = (): void => setPageHeight(window.innerHeight);
            const observer = new MutationObserver(() => {
                reevaluateStickiness();
            });

            observer.observe(document.body, { childList: true, subtree: true });
            window.addEventListener("resize", handleWindowResize);

            return () => {
                window.removeEventListener("resize", handleWindowResize);
                observer.disconnect();
            };
        }
    }, [enabled]);

    //handle subnav height
    useEffect(() => {
        if (enabled) {
            setNavHeight(subNavRef?.current?.clientHeight ?? 0);
        }
    }, [window.location.href, subNavRef?.current, subNavRef?.current?.clientHeight]);

    //handle permanent stickiness
    useEffect(() => {
        if (enabled) {
            reevaluateStickiness();
        }
    }, [window.location.href, pageHeight, navHeight]);

    //automatically close the subnav after a fixed delay
    useEffect(() => {
        if (enabled) {
            if (scrollDelta < 0) {
                clearTimeout(closeTimeout);
                setCloseTimeout(
                    setTimeout(() => {
                        setScrollDelta(0);
                        setCloseTimeout(null);
                    }, CLOSE_DELAY),
                );
            } else {
                setCloseTimeout(null);
            }
            return () => clearTimeout(closeTimeout);
        }
    }, [scrollDelta]);

    const reevaluateStickiness = () => {
        const largeScreen = pageHeight > MAX_DYNAMIC_STICKINESS_HEIGHT;
        const isFilterOpen = FILTER_POPUP_SELECTORS.some((selector) => !!document.body.querySelector(selector));
        setAlwaysSticky(largeScreen || isFilterOpen);
    };

    const handleScroll = useCallback(
        (ev: UIEvent<HTMLDivElement>): void => {
            if (!enabled || alwaysSticky) {
                return;
            }
            const currScrollHeight = ev.currentTarget.scrollTop;
            setLastScrollHeight(currScrollHeight);
            setScrollDelta(currScrollHeight - lastScrollHeight);
        },
        [alwaysSticky, enabled, lastScrollHeight],
    );

    const SubNavWrapper = useCallback(
        (props: ISubNavWrapper) =>
            enabled ? (
                <StickySubNav
                    {...props}
                    ref={props.subNavRef}
                    className={props.className}
                    isSticky={props.isSticky}
                >
                    {props.children}
                </StickySubNav>
            ) : (
                <div {...props}>{props.children}</div>
            ),
        [],
    );

    const PageContentWrapper = useCallback(
        (props: IPageContentWrapper) =>
            enabled ? (
                <div
                    {...props}
                    ref={pageContentRef}
                    onScroll={props.handleScroll}
                >
                    <div
                        style={{
                            height: props.navHeight,
                            width: "100%",
                        }}
                    />
                    {props.children}
                </div>
            ) : (
                <div
                    {...props}
                    ref={pageContentRef}
                >
                    {props.children}
                </div>
            ),
        [],
    );

    const subNavWrapperProps = useMemo(() => ({ isSticky, subNavRef }), [isSticky, subNavRef]);

    const pageContentWrapperProps = useMemo(() => ({ handleScroll, navHeight }), [handleScroll, navHeight]);

    return {
        SubNavWrapper,
        PageContentWrapper,
        subNavWrapperProps,
        pageContentWrapperProps,
    };
};

export default useStickySubNav;
